import { z } from 'zod';
import { bcp47, jsonObject, timestamp, uuid } from './common.js';
/**
 * Documents (core, §4). `documents`, `document_versions` (immutable DAG),
 * `document_outlines` (F15 confirm gate), `templates`, `personas`.
 */
/** F4 reading-difficulty attribute of a document (distinct from cost complexity). */
export const difficultyLevel = z.enum(['basic', 'intermediate', 'advanced', 'mastery']);
export const documentKind = z.enum(['book', 'article', 'report']);
export const documentStatus = z.enum(['draft', 'generating', 'ready', 'failed']);
export const documentSchema = z.object({
    id: uuid,
    ownerId: uuid,
    title: z.string().min(1).max(300),
    kind: documentKind,
    difficultyLevel,
    currentVersionId: uuid.nullable(),
    templateId: uuid.nullable(),
    personaId: uuid.nullable(),
    sourceBookId: uuid.nullable(), // F5 public-domain corpus
    language: bcp47, // BCP-47
    status: documentStatus,
    isPublic: z.boolean(),
    createdAt: timestamp,
    updatedAt: timestamp,
    deletedAt: timestamp.nullable(),
});
// --- manuscript / outline jsonb shapes (mirror the bookforge engine) ----------
/**
 * Defense-in-depth ceiling on a chapter's extracted/generated body (mirrored
 * by `apps/workers/src/parsing/normalize.ts`'s `MAX_EXTRACTED_CHARS` — keep
 * both in sync): no code path — ingest parsing, generation, a future bug —
 * may ever write an unbounded string into this jsonb column.
 */
const MAX_CHAPTER_BODY_CHARS = 5_000_000;
/** One chapter of a rendered manuscript (`document_versions.contentJsonb`). */
export const manuscriptChapter = z.object({
    id: z.string().min(1),
    order: z.number().int().nonnegative(),
    title: z.string(),
    body: z.string().max(MAX_CHAPTER_BODY_CHARS),
});
export const manuscriptJsonb = z.object({
    title: z.string().optional(),
    chapters: z.array(manuscriptChapter),
    meta: jsonObject.optional(),
});
export const outlineNode = z.lazy(() => z.object({
    id: z.string().min(1),
    order: z.number().int().nonnegative(),
    title: z.string(),
    summary: z.string(),
    children: z.array(outlineNode).optional(),
}));
export const outlineJsonb = z.object({
    title: z.string().optional(),
    nodes: z.array(outlineNode),
});
/** Which action produced a version (the DAG edge label). */
export const versionOrigin = z.enum([
    'generate',
    'expand',
    'cut_continue',
    'difficulty_elevate',
    'translate',
    'edit', // F67 autosave
    'restyle', // F65 re-render (no text regen)
    'imported', // F18/F6 ingest-worker: parsed from an uploaded/Drive-imported source file
]);
export const documentVersionSchema = z.object({
    id: uuid,
    documentId: uuid,
    parentVersionId: uuid.nullable(), // immutable DAG key
    origin: versionOrigin,
    language: bcp47,
    contentJsonb: manuscriptJsonb,
    outlineJsonb: outlineJsonb.nullable(),
    /** Job that produced this version — idempotency guard against re-runs (E11). */
    producedByJobId: uuid.nullable(),
    /**
     * F67 `editor.save` autosave idempotency key — null for every non-`edit`
     * origin (job-produced versions dedupe via `producedByJobId` instead).
     * Optional so pre-M3-shaped objects built without this field remain valid.
     */
    clientMutationId: uuid.nullable().optional(),
    createdAt: timestamp,
});
export const outlineStatus = z.enum(['proposed', 'confirmed', 'rejected']);
export const documentOutlineSchema = z.object({
    id: uuid,
    documentId: uuid,
    status: outlineStatus,
    proposedJsonb: outlineJsonb,
    confirmedJsonb: outlineJsonb.nullable(),
    producedByJobId: uuid.nullable(),
    createdAt: timestamp,
    confirmedAt: timestamp.nullable(),
});
export const templateSchema = z.object({
    id: uuid,
    name: z.string().min(1).max(160),
    description: z.string().max(2000).nullable(),
    kind: documentKind,
    /** Structure/config the generator consumes. */
    structureJsonb: jsonObject,
    isSeed: z.boolean(),
    isActive: z.boolean(),
    createdAt: timestamp,
});
export const personaSchema = z.object({
    id: uuid,
    /** Null for a seeded/public persona; otherwise the owning user. */
    ownerId: uuid.nullable(),
    name: z.string().min(1).max(160),
    description: z.string().max(2000).nullable(),
    /** Voice/style directives applied at generation time. */
    styleJsonb: jsonObject,
    isPublic: z.boolean(),
    isSeed: z.boolean(),
    createdAt: timestamp,
    updatedAt: timestamp,
});
